import {
    signInWithPhoneNumber,
    RecaptchaVerifier,
    onAuthStateChanged,
    signOut,
    GoogleAuthProvider,
    signInWithPopup
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Configure et initialise le vérificateur reCAPTCHA de Firebase.
 * @param {string} containerId - L'ID de l'élément DOM où le reCAPTCHA doit être rendu.
 */
export const setupRecaptcha = (containerId) => {
    console.log('setupRecaptcha', containerId, window.recaptchaVerifier);
    // Nettoyer toute instance précédente pour éviter les conflits.
    if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
    }

    // Créer une nouvelle instance.
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        'size': 'invisible',
        'callback': () => {
            console.log('reCAPTCHA résolu.');
        },
        'expired-callback': () => {
            console.warn('La vérification reCAPTCHA a expiré.');
        }
    });

    // Rendre le widget. C'est une étape nécessaire.
    return window.recaptchaVerifier.render();
};

/**
 * Envoie le code de vérification par SMS au numéro fourni.
 * @param {string} phoneNumber - Le numéro de téléphone complet de l'utilisateur.
 */
export const sendVerificationCode = async (phoneNumber) => {
    try {
        console.log('Auth Service - Tentative d\'envoi du code à :', phoneNumber);

        if (!window.recaptchaVerifier) {
            throw new Error("reCAPTCHA n'a pas été initialisé. Assurez-vous d'appeler setupRecaptcha.");
        }
        // Note : la vérification reCAPTCHA Enterprise pour le Phone Auth est gérée
        // automatiquement par Firebase via appVerifier (RecaptchaVerifier), il ne faut
        // pas appeler manuellement grecaptcha.enterprise.execute ici (cela entrait en
        // conflit avec la vérification interne de Firebase et provoquait des erreurs 400/503).
        const appVerifier = window.recaptchaVerifier;
        const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
        
        // Stocker le résultat pour l'utiliser sur l'écran de vérification du code.
        window.confirmationResult = confirmationResult;
        console.log('Auth Service - Code de vérification envoyé avec succès.');
        return confirmationResult;

    } catch (error) {
        console.error('Auth Service - Erreur lors de l\'envoi du code:', error);
        throw error; // Renvoyer l'erreur pour que le composant puisse la gérer.
    }
};

/**
 * Vérifie le code OTP (One-Time Password) entré par l'utilisateur.
 * @param {string} code - Le code à 6 chiffres.
 */
export const verifyCode = async (code) => {
    try {
        if (!window.confirmationResult) {
            throw new Error('Aucun résultat de confirmation disponible. Veuillez demander un nouveau code.');
        }

        const result = await window.confirmationResult.confirm(code);
        
        // Nettoyage après une vérification réussie.
        window.confirmationResult = null;
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear();
        }
        
        console.log('Auth Service - Code vérifié avec succès, utilisateur:', result.user);
        return result;

    } catch (error) {
        console.error('Auth Service - Erreur lors de la vérification du code:', error);
        throw error;
    }
};

/**
 * Connecte l'utilisateur via une popup Google (Firebase Auth).
 * Utilisable indifféremment depuis un écran de connexion ou d'inscription :
 * Firebase crée le compte Google automatiquement s'il n'existe pas encore.
 * @returns {Promise<UserCredential>} - Le résultat de connexion Firebase (contient .user)
 */
export const signInWithGoogle = async () => {
    try {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        console.log('Auth Service - Connexion Google réussie.');
        return result;
    } catch (error) {
        console.error('Auth Service - Erreur lors de la connexion avec Google:', error);
        throw error;
    }
};

/**
 * Vérifie l'état de connexion de l'utilisateur
 * @returns {Promise<User|null>} - L'utilisateur connecté ou null
 */
export const getCurrentUser = () => {
    return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
        });
    });
};

/**
 * Déconnecte l'utilisateur actuellement connecté
 * @returns {Promise<void>}
 */
export const logoutUser = async () => {
    try {
        await signOut(auth);
        console.log('Auth Service - Utilisateur déconnecté avec succès');
        
        // Nettoyer les données temporaires
        window.confirmationResult = null;
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear();
        }
        
        return true;
    } catch (error) {
        console.error('Auth Service - Erreur lors de la déconnexion:', error);
        throw error;
    }
};

/**
 * Écoute les changements d'état d'authentification
 * @param {function} callback - Fonction appelée lors des changements d'état
 * @returns {function} - Fonction pour désabonner l'écoute
 */
export const onAuthStateChange = (callback) => {
    return onAuthStateChanged(auth, callback);
};
